import { ethers } from 'ethers';
import BigNumber from 'bignumber.js';
import { StakingPool } from 'api/StakingAPI';
import { calculateAverageAPR } from 'utils/aprCalculation';

type ContractReadResult = {
  status: 'success' | 'failure';
  result?: unknown;
};

/**
 * Transforms contract read results into StakingPool data
 */
export const transformPoolDataFromContracts = (
  contractData: ContractReadResult[] | undefined,
  pool: StakingPool
): StakingPool => {
  if (!contractData || contractData.length === 0) {
    return pool;
  }

  try {
    // Extract staking contract metadata (first 12 calls)
    const stakingMetadata = extractStakingMetadata(contractData, pool);

    // Get total volume locked (ERC20 balanceOf) - index 12 if depositTokenAddress exists
    const balanceOfIndex = pool.depositTokenAddress ? 12 : -1;
    const totalVolumeLocked = extractTotalVolumeLocked(
      contractData,
      balanceOfIndex
    );

    // Get rewards data
    const getRewardInfoIndex = balanceOfIndex >= 0 ? 14 : 13;
    const commissionIndex = balanceOfIndex >= 0 ? 15 : 14;

    const { totalRewards, rewardsEmissionPeriod, endOfEmission } =
      extractRewardsData(contractData, getRewardInfoIndex);

    const rewardsCommissionPercentage = extractRewardsCommission(
      contractData,
      commissionIndex
    );

    // Calculate average APR
    const averageAPR = calculateAverageAPRFromData(
      totalVolumeLocked,
      rewardsEmissionPeriod,
      totalRewards,
      rewardsCommissionPercentage,
      endOfEmission
    );

    return {
      ...pool,
      rewardsCommissionPercentage: rewardsCommissionPercentage ?? '0',
      tokensStaked: ethers.utils.formatUnits(
        totalVolumeLocked,
        pool.depositTokenDecimals
      ),
      rewardsEmissionPeriod: rewardsEmissionPeriod,
      averageAPR: averageAPR,
      totalRewards: totalRewards,
      totalReputationScore: stakingMetadata.totalScore,
      MAX_LOCKING_PERIOD: stakingMetadata.MAX_LOCKING_PERIOD,
      earlyUnlockFeePerDayPercentage: stakingMetadata.earlyUnlockFeePerDay,
      earlyUnlockFeesPercentage: stakingMetadata.earlyUnlockFees,
    };
  } catch (error) {
    console.error('Error processing pool data:', error);
    return getDefaultPoolData(pool);
  }
};

/**
 * Extracts staking contract metadata from contract results
 */
const extractStakingMetadata = (
  contractData: ContractReadResult[],
  pool: StakingPool
) => {
  return {
    MAX_EARLY_UNLOCK_FEE: safeExtractBigNumber(contractData[0]) ?? '0',
    MAX_EARLY_UNLOCK_FEE_PER_DAY: safeExtractBigNumber(contractData[1]) ?? '0',
    MAX_LOCKING_PERIOD: safeExtractBigNumber(contractData[2]) ?? '0',
    MAX_PERCENTAGE: safeExtractBigNumber(contractData[3]) ?? '0',
    claim: safeExtractString(contractData[4]) ?? '',
    depositTokenAddress:
      safeExtractString(contractData[5]) ?? pool.depositTokenAddress,
    earlyUnlockFeePerDay: safeExtractBigNumber(contractData[6]) ?? '0',
    earlyUnlockFees: safeExtractBigNumber(contractData[7]) ?? '0',
    ownerAddress: safeExtractString(contractData[8]) ?? '',
    rewardTokenAddress: safeExtractString(contractData[9]) ?? '',
    rewardsContractAddress:
      safeExtractString(contractData[10]) ?? pool.rewardsContractAddress,
    totalScore: safeExtractBigNumber(contractData[11]) ?? '0',
  };
};

/**
 * Extracts total volume locked from ERC20 balanceOf call
 */
const extractTotalVolumeLocked = (
  contractData: ContractReadResult[],
  balanceOfIndex: number
): string => {
  if (
    balanceOfIndex >= 0 &&
    contractData[balanceOfIndex]?.status === 'success' &&
    contractData[balanceOfIndex].result != null
  ) {
    return ethers.BigNumber.from(
      contractData[balanceOfIndex].result
    ).toString();
  }
  return '0';
};

/**
 * Extracts rewards data from contract results
 */
const extractRewardsData = (
  contractData: ContractReadResult[],
  getRewardInfoIndex: number
) => {
  const rewardInfo =
    contractData[getRewardInfoIndex]?.status === 'success' &&
    contractData[getRewardInfoIndex].result != null
      ? (contractData[getRewardInfoIndex].result as {
          emissionPeriod: string;
          endOfEmission: string;
          totalAmount: string;
        })
      : null;
  const rewardsEmissionPeriod =
    rewardInfo?.emissionPeriod != null
      ? String(rewardInfo.emissionPeriod)
      : '0';
  const endOfEmission =
    rewardInfo?.endOfEmission != null ? String(rewardInfo.endOfEmission) : '0';
  const totalRewards =
    rewardInfo?.totalAmount != null ? String(rewardInfo.totalAmount) : '0';

  return { totalRewards, rewardsEmissionPeriod, endOfEmission };
};

/**
 * Extracts rewards commission percentage from contract results
 */
const extractRewardsCommission = (
  contractData: ContractReadResult[],
  commissionIndex: number
): string => {
  if (
    contractData[commissionIndex]?.status === 'success' &&
    contractData[commissionIndex].result != null
  ) {
    return new BigNumber(String(contractData[commissionIndex].result))
      .dividedBy(100)
      .toString();
  }
  return '0';
};

/**
 * Calculates average APR from extracted data
 */
const calculateAverageAPRFromData = (
  totalVolumeLocked: string,
  rewardsEmissionPeriod: string,
  totalRewards: string,
  rewardsCommissionPercentage: string,
  endOfEmission: string
): string => {
  let averageAPR = calculateAverageAPR({
    tokensLocked: totalVolumeLocked,
    rewardsEmissionPeriod: rewardsEmissionPeriod,
    totalReward: new BigNumber(totalRewards)
      .times(
        new BigNumber(1).minus(
          new BigNumber(rewardsCommissionPercentage).dividedBy(100)
        )
      )
      .toFixed(),
    endOfEmission,
  });

  if (averageAPR instanceof Error) {
    averageAPR = '0';
  }

  return averageAPR;
};

/**
 * Safely extracts a BigNumber from contract result
 */
const safeExtractBigNumber = (
  result: ContractReadResult | undefined
): string | null => {
  if (result?.status === 'success' && result.result != null) {
    try {
      return ethers.BigNumber.from(result.result).toString();
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Safely extracts a string from contract result
 */
const safeExtractString = (
  result: ContractReadResult | undefined
): string | null => {
  if (result?.status === 'success' && result.result != null) {
    return String(result.result);
  }
  return null;
};

/**
 * Returns default pool data in case of error
 */
const getDefaultPoolData = (pool: StakingPool): StakingPool => {
  return {
    ...pool,
    rewardsCommissionPercentage: '0',
    tokensStaked: '0',
    rewardsEmissionPeriod: '0',
    averageAPR: '0',
    totalRewards: '0',
    totalReputationScore: '0',
  };
};
